import { useTranslation } from "react-i18next";
import { stdObject } from "../../../../data/types/stdObject";
import {
  activeOptions,
  getType,
  monthsOptions,
  sexesOptions,
  statusComm,
  statusOptions,
  statusPayment,
  headersOptions,
  servicesAreas,
  languageOptions,
  adminAreas,
  stationeryOptions,
  accountingAreas,
  webOptions,
  branchAreas,
  generalOptions,
} from "../data/searchItemUtils";
import { RootState } from "../../../../store/store";
import { useSelector } from "react-redux";
import { useStructureContext } from "../../../../context/StructureContext";
import { extraListCodes } from "../../../../data/main";

export default function useSearchModule({ data = {} }: { data: stdObject }) {
  const {
    type,
    section,
    selectOptions,
    searchValues,
    updateSearchValues,
    catList,
  } = useStructureContext();
  const { t } = useTranslation();
  const user: any = useSelector((state: RootState) => state.data.user);
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const searchOptions: any = useSelector(
    (state: RootState) => state.data.search,
  );
  const usersList: stdObject = useSelector(
    (state: RootState) => state.data.usersList,
  );
  const rolsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.rol,
  );
  const schedulesList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.hor,
  );
  const formatsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.for,
  );

  const bankAccountsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.bankAccounts,
  );

  const { CODCAB, TITCAB, MODCAB, MSECAB = true } = data;

  const base = section.code;

  const getValue = (value: string, code: string = CODCAB) =>
    searchValues[section.id][code]?.[value] ?? "";

  const changeValue = ({
    key = CODCAB,
    type = 0,
    value,
    valueKey = "value",
    mode = 0,
  }: {
    key?: string;
    type?: number;
    value?: string;
    valueKey?: string;
    mode?: number;
  }) => {
    updateSearchValues({
      key,
      type,
      value,
      valueKey,
      mode,
    });
  };

  let tempData: stdObject = { type: "select" };
  switch (CODCAB) {
    case "PREART":
    case "ALMART":
    case "PREPCO":
    case `NET${base}`:
    case `IMP${base}`:
    case `TOT${base}`:
    case `SAL${base}`:
    case `ABO${base}`:
    case `CAD${base}`:
    case "BULART":
      tempData.type = "numberRange";
      tempData.options = ["PREART", "ALMART"].includes(CODCAB)
        ? selectOptions[CODCAB]
        : null;
      break;
    // case "SERART":
    case "ENSART":
    case "EDIART":
    case `WHA${base}`:
    case `WEB${base}`:
    case `GPS${base}`:
    case `ADJ${base}`:
    case "OTRART":
      tempData.options = activeOptions.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `GEN${base}`:
      tempData.options = [
        { value: "", label: t("p.all") },
        ...sexesOptions.map((e) => {
          return { ...e, label: t(e.label) };
        }),
      ];
      break;
    case `NAC${base}`:
      tempData.options = monthsOptions.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `EST2${base}`:
      tempData.options = statusPayment.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `ESTD2${base}`:
      tempData.options = statusComm.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `CODADM`:
      tempData.options = adminAreas.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `CODISU`:
      tempData.options = servicesAreas.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `CODACO`:
      tempData.options = accountingAreas.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `CODFOR`:
      tempData.options = [
        { value: "", label: t("p.all") },
        ...formatsList.map((e) => {
          return { label: e.NOMFOR, value: e.CODFOR };
        }),
      ];
      break;
    case `CODSUC`:
      tempData.options = branchAreas.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `EST3${base}`:
      tempData.options = statusOptions.map((e) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case `PSE${base}`:
      tempData.options = [
        { label: t("p.all"), value: "" },
        { label: t("p.none"), value: 0 },
        ...searchOptions.docInsurances.map((e: stdObject) => {
          return { ...e, label: e.NOMPSE, value: e.CODPSE };
        }),
      ];
      break;
    case "FAMART":
      tempData.options = catList;
      break;
    case "MONSEV":
      tempData.type = "month";
      break;
    case "FCRART":
    case "FLLART":
    case "FMOART":
    case `FEC${base}`:
    case `FEC2${base}`:
      tempData.type = "date";
      break;
    case `TIM${base}`:
      tempData.options = [
        { label: t("p.all"), value: "" },
        ...searchOptions.docTime.map((e: stdObject) => {
          return {
            ...e,
            label: e.NOMTIM,
            value: [e.TIPTIM, e.LA1TIM, e.LA2TIM],
          };
        }),
      ];
      break;
    case "AREHIS":
      const extraAreaData =
        "results" in searchValues[section.id]
          ? searchValues[section.id].results.reduce(
              (acc: Array<stdObject>, curr: stdObject) => {
                if (
                  !acc.find(
                    (o: stdObject) =>
                      o.value === curr.type ||
                      (!curr.type && o.value === "undefined"),
                  )
                ) {
                  acc.push({
                    label: curr.typeName,
                    value: curr.type ?? "undefined",
                  });
                }
                return acc;
              },
              [],
            )
          : [];
      tempData.options = [{ label: t("p.all"), value: "" }, ...extraAreaData];
      break;
    case "PERHIS":
      const extraUserData =
        "results" in searchValues[section.id]
          ? searchValues[section.id].results.reduce(
              (acc: Array<stdObject>, curr: stdObject) => {
                if (
                  !acc.find(
                    (o: stdObject) =>
                      o.value === curr.userCode ||
                      (!curr.userCode && o.value === "undefined"),
                  )
                ) {
                  acc.push({
                    label: curr.userName,
                    value: curr.userCode ?? "undefined",
                  });
                }
                return acc;
              },
              [],
            )
          : [];
      tempData.options = [{ label: t("p.all"), value: "" }, ...extraUserData];
      break;
    case "ACNHIS":
      const extraActionData =
        "results" in searchValues[section.id]
          ? searchValues[section.id].results.reduce(
              (acc: Array<stdObject>, curr: stdObject) => {
                if (
                  !acc.find(
                    (o: stdObject) =>
                      o.value === curr.observ ||
                      (!curr.observ && o.value === "undefined"),
                  )
                ) {
                  acc.push({
                    label: curr.action,
                    value: curr.observ ?? "undefined",
                  });
                }
                return acc;
              },
              [],
            )
          : [];
      tempData.options = [{ label: t("p.all"), value: "" }, ...extraActionData];
      break;
    case `PER${base}`:
      tempData.options =
        section.id === "D2" && userAccess[section.id[0]][section.id] === 2
          ? [{ label: user.NOMUSU, value: user.CODUSU }]
          : [
              { label: t("p.all"), value: "" },
              ...Object.keys(usersList)
                .map((e: string) => {
                  return {
                    label: usersList[e].NOMUSU,
                    value: e,
                  };
                })
                .sort((a: stdObject, b: stdObject) =>
                  a.label.localeCompare(b.label),
                ),
            ];
      break;
    case `PAG${base}`:
      tempData.options = [
        { label: t("p.all"), value: "" },
        ...searchOptions.docStatus
          .filter((e: stdObject) => e.ACTPAG === 1)
          .map((e: stdObject) => {
            return { ...e, label: e.NOMPAG, value: e.CODPAG };
          }),
      ];
      break;
    case `ROL${base}`:
      tempData.options = [
        { label: t("p.all"), value: "" },
        { label: t("p.none"), value: 0 },
        ...rolsList
          .map((e: stdObject) => {
            return {
              label: e.NOMROL,
              value: e.CODROL,
            };
          })
          .sort((a: stdObject, b: stdObject) => a.label.localeCompare(b.label)),
      ];
      break;
    case "FIEPER":
      tempData.options = extraListCodes.map((e: string) => {
        return { label: t(`c.${e}`), value: e };
      });
      break;
    case "GRUCAB":
      tempData.options = headersOptions.map((e: stdObject) => {
        return { label: t(e.label), value: e.value };
      });
      break;
    case `USU${base}`:
      tempData.options = [{ label: t("p.all"), value: 0 }];
      break;
    case "GRULAN":
      tempData.options = languageOptions.map((e: stdObject) => {
        return { label: t(e.label), value: e.value };
      });
      break;
    case "SECTSTA":
      tempData.options = stationeryOptions.map((e: stdObject) => {
        return { label: t(e.label), value: e.value };
      });
      break;
    case "CODWEB":
      tempData.options = webOptions.map((e: stdObject) => {
        return { label: t(e.label), value: e.value };
      });
      break;
    case "SCHIRE":
      tempData.options = [
        { label: "", value: "" },
        ...schedulesList.map((e: stdObject) => {
          return { label: e.NOMHOR, value: e.CODHOR };
        }),
      ];
      break;
    case "CODGEN":
      tempData.options = generalOptions.map((e: stdObject) => {
        return { ...e, label: t(e.label) };
      });
      break;
    case "HORIRE":
      tempData.type = "time";
      break;
    case "FEC3IRE":
      tempData.type = "oneDate";
      break;
    case "CUEICA":
      tempData.options = [
        { label: t("p.cash"), value: 0 },
        ...bankAccountsList.map((e: stdObject) => {
          return { label: e.NOMCUE, value: e.CODCUE };
        }),
      ];
      break;
    default:
      tempData.type = getType(CODCAB, type);
      tempData.mode = MODCAB;
  }
  return {
    type,
    section,
    tempData,
    CODCAB,
    TITCAB,
    MSECAB,
    changeValue,
    getValue,
  };
}
